import React, {
    useEffect,
    useState
} from "react";
import {
    useParams,
    useSearchParams,
    Link
} from "react-router-dom";
import Config from "../utils/Config";
import {
    fetchFromAPI,
    normalizeUrl
} from "../utils/functions";
import { buildUrl } from "../utils/buildUrl";
import useTitle from "../hooks/useTitle";
import "./BlogPost.css";

const imgSrc = (post) => {
    try {
        if (post?.coverImagePath) return buildUrl(post.coverImagePath);
    } catch { }
    return Config.CONST.PLACEHOLDER;
};

const onImgError = (e) => {
    if (e.currentTarget.dataset.fallbackApplied) return;
    e.currentTarget.dataset.fallbackApplied = "1";
    e.currentTarget.src = Config.CONST.PLACEHOLDER;
};

export default function BlogPost() {
    const { blogId } = useParams();
    const [searchParams] = useSearchParams();
    const dealer = searchParams.get("dealer") || "";
    const site = searchParams.get("site") || "";

    const [post, setPost] = useState(null);
    const [err, setErr] = useState("");
    useTitle(post?.title ?? "Loading…");

    useEffect(() => {
        let ignore = false;
        (async () => {
            try {
                const { data } = await fetchFromAPI(
                    `${Config.ENDPOINTS.BLOG_BY_SLUG}/${blogId}`,
                    { method: "GET" }
                );
                if (!ignore) setPost(data);
            } catch (e) {
                if (!ignore) setErr("Post not found.");
            }
        })();
        return () => {
            ignore = true;
        };
    }, [blogId]);

    if (err)
        return (
            <div className="blog-wrapper">
                <p className="blog-error">{err}</p>
            </div>
        );
    if (!post)
        return (
            <div className="blog-wrapper">
                <div className="blog-loading">Loading…</div>
            </div>
        );

    const backQuery = new URLSearchParams({
        ...(dealer ? { dealer } : {}),
        ...(site ? { site } : {}),
    }).toString();

    return (
        <div className="blog-container">
            <div className="login-box">
                <article className="post-wrapper">
                    <header className="post-head">
                        <div className="blog-nav-button">
                            <div className="post-crumbs">
                                <Link
                                    className="crumb-back"
                                    to={`/blog${backQuery ? `?${backQuery}` : ""}`}
                                    aria-label="Back to all posts"
                                >
                                    All posts
                                </Link>
                            </div>
                            <div className="post-crumbs">
                                <a
                                    className="crumb-back"
                                    href={normalizeUrl(site)}   // site might be "guns.ffls.io:5173" or already have https
                                    aria-label="Back to all posts"
                                >
                                    Back to website
                                </a>
                            </div>
                        </div>
                        <h1>{post.title}</h1>
                        <p className="post-date">
                            {new Date(post.publishedAt || post.createdAt).toLocaleString()}
                        </p>
                    </header>

                    {/* Always render cover with fallback (no broken images) */}
                    <div className="post-cover">
                        <img
                            src={imgSrc(post)}
                            alt={post.title}
                            loading="lazy"
                            decoding="async"
                            onError={onImgError}
                        />
                    </div>

                    <div
                        className="post-content"
                        dangerouslySetInnerHTML={{ __html: post.content || "" }}
                    />
                </article>

                {(Array.isArray(post.tags) && post.tags.length > 0) || true ? (
                    <div className="post-meta-bar">
                        <div className="post-tags">
                            {(post.tags || []).map((t) => (
                                <span key={t} className="post-tag">
                                    {t}
                                </span>
                            ))}
                        </div>
                        <div className="post-share">
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                                    post.title
                                )}&url=${encodeURIComponent(window.location.href)}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Share
                            </a>
                            <a href="#">Copy link</a>
                        </div>
                    </div>
                ) : null}

                {(post.author || post.authorBio || post.authorAvatar) && (
                    <section className="post-author">
                        <img
                            className="avatar"
                            src={
                                post.authorAvatar ? buildUrl(post.authorAvatar) : "/assets/author.png"
                            }
                            alt={post.author || "Author"}
                        />
                        <div>
                            <h4>{post.author || "FFLS.io"}</h4>
                            <p>
                                {post.authorBio || "News, tips, and guides from the FFLS.io team."}
                            </p>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}